import { FormattedMessage, useIntl } from "react-intl";
import { InlineEdit } from "@/components/design/InlineEdit";
import type { FieldDiff, MergeSide, ResolutionChoice } from "@/components/save/profile-merge-types";
import { MergeGuideControls } from "@/components/save/ProfileMergeGuide";
import { MergeDateWarning } from "@/components/save/MergeDateWarning";
import { MergeFieldWarning } from "@/components/save/MergeFieldWarning";
import { useDateFieldWarning } from "@/components/save/use-date-field-warning";
import { useImportFieldWarning } from "@/components/save/use-import-field-warning";
import { useGuideCard } from "@/components/save/use-guide-card";
import {
  useProfileMergeStore,
  defaultChoice,
  detailDiffFor,
  pristineValue,
  replacesSavedValue,
} from "@/stores/profile-merge-store";

// In-line LinkedIn merge affordance for a single profile-detail field. Rendered
// directly UNDER the field it belongs to: it shows the compared pair — the
// user's existing value and the incoming LinkedIn value — labelled by
// whether it fills an empty field (ADD) or conflicts with a different existing
// value (CONFLICT), and lets the user choose per field which side to take.
//
// The choice is made through the guided review (ProfileMergeGuide): when this
// card is the guide's active step it carries the decision buttons (deciding
// advances the guide); otherwise it states its current resolution with a
// Review button, and any click/focus inside the card pulls the guide here.
//
// BOTH sides of the pair are editable in place (the ORIGINAL field above stays
// locked during a review — it keeps showing what is saved in the system).
// Editing a side selects it; a per-side reset appears once a side was edited
// and reverts it to its pristine value. Choices and edits are recorded in the
// merge store; the save applies the per-item resolutions through the canonical
// profile mutations, carrying an edited chosen value as `overrideValue`.
//
// Renders nothing when there is no active preview, no diff for this field, or the
// field is UNCHANGED — so it is safe to drop under every field unconditionally.

const SIDE_CHOICE: Record<MergeSide, ResolutionChoice> = {
  existing: "KEEP_EXISTING",
  incoming: "USE_INCOMING",
};

const SIDE_LABEL = {
  existing: { id: "profile.merge.side.existing", defaultMessage: "Your value" },
  incoming: { id: "profile.merge.incomingLabel", defaultMessage: "Imported" },
} as const;

const EDIT_ARIA = {
  existing: { id: "profile.merge.editAria.existing", defaultMessage: "Edit your value" },
  incoming: { id: "profile.merge.editAria.incoming", defaultMessage: "Edit the imported value" },
} as const;

const RESET_ARIA = {
  existing: { id: "profile.merge.reset.existing", defaultMessage: "Reset to your saved value" },
  incoming: { id: "profile.merge.reset.incoming", defaultMessage: "Reset to the imported value" },
} as const;

function MergeSideValue({
  diff,
  side,
  discarded,
  type,
  multiline,
  editable,
  format,
}: {
  diff: FieldDiff;
  side: MergeSide;
  discarded: boolean;
  type: "text" | "date" | "number" | "email" | "tel";
  multiline: boolean;
  // A reference/multi-value field (gender, citizenship, …) is read-only: it
  // stores an id (or id-list) shown via `format` as a label, and is chosen with
  // the keep/use buttons rather than typed.
  editable: boolean;
  format?: (value: string) => string;
}) {
  const intl = useIntl();
  const edited = useProfileMergeStore((s) => s.edits[diff.key]?.[side]);
  const setEdit = useProfileMergeStore((s) => s.setEdit);
  const clearEdit = useProfileMergeStore((s) => s.clearEdit);
  const setChoice = useProfileMergeStore((s) => s.setChoice);

  const pristine = pristineValue(diff, side) ?? "";
  const value = edited ?? pristine;
  const dirty = editable && edited !== undefined && edited !== pristine;
  // A date field (dateOfBirth) is validated on the side that would be committed
  // (not the discarded one; the existing side only once edited). Native <input
  // type="date"> already blocks a bad month/day, so "future" is the reachable
  // case — but the validator covers both.
  const validateDate = editable && type === "date" && !discarded && (side === "incoming" || dirty);
  const dateWarn = useDateFieldWarning(value, validateDate);
  // Length / email / phone validation (L2 mirror of the genesis L1 sanitizer), on the same
  // committed-side basis as the date warning. importFieldError is a no-op for fields with no rule.
  const validateField = editable && !discarded && (side === "incoming" || dirty);
  const fieldWarn = useImportFieldWarning(diff.key, value, validateField);
  // Read-only sides render the machine value through `format` (id → label).
  const display = format ? format(value) : value;

  const onChange = (next: string) => {
    // InlineEdit's Escape-revert fires onChange with the unchanged value when
    // the user never typed — don't let that flip the choice.
    if (next === value) return;
    dateWarn.onEdit();
    fieldWarn.onEdit();
    setEdit(diff.key, side, next);
    // Editing a side is choosing it — the edit is the value the commit takes.
    // EXCEPT a blank keystroke: emptying a value reads as rejecting it, so it
    // must not select the side (blur then snaps the value back to pristine).
    if (next.trim() !== "") setChoice(diff.key, SIDE_CHOICE[side]);
  };
  const onCommit = (committed: string) => {
    dateWarn.onBlur();
    fieldWarn.onBlur();
    // A blank or pristine-equal edit is no override: snap back to the snapshot
    // value (a blank override would be a destructive clear the preview never
    // offers). Deliberate undo is the reset button.
    if (committed.trim() === "" || committed === pristine) clearEdit(diff.key, side);
  };

  return (
    <div className={discarded ? "opacity-45" : ""} id={`profile-merge-side-${side}-${diff.key}`}>
      <div className="flex items-center gap-1.5">
        <span className={`smycles-field-meta text-ink-muted ${discarded ? "line-through" : ""}`}>
          <FormattedMessage {...SIDE_LABEL[side]} />
        </span>
        {dirty ? (
          <button
            type="button"
            onClick={() => clearEdit(diff.key, side)}
            aria-label={intl.formatMessage(RESET_ARIA[side])}
            title={intl.formatMessage(RESET_ARIA[side])}
            id={`profile-merge-reset-${side}-${diff.key}`}
            className="flex h-4 w-4 items-center justify-center rounded-full text-ink-muted transition hover:bg-ink/10 hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink-ui"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-3 w-3"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
            </svg>
          </button>
        ) : null}
      </div>
      {editable ? (
        <>
          <InlineEdit
            id={`profile-merge-input-${side}-${diff.key}`}
            value={value}
            onChange={onChange}
            onCommit={onCommit}
            type={type}
            multiline={multiline}
            // Long single-logical-line values (a pasted employer name, a URL) wrap
            // inside the card instead of growing the input past its bounds.
            wrap={!multiline && type === "text"}
            clearable={false}
            ariaLabel={intl.formatMessage(EDIT_ARIA[side])}
            className="smycles-field-value min-w-0"
            inputClassName="smycles-field-value"
          />
          <MergeDateWarning id={`profile-merge-input-${side}-${diff.key}-date-warning`} error={dateWarn.error} />
          <MergeFieldWarning id={`profile-merge-input-${side}-${diff.key}-field-warning`} fieldKey={diff.key} error={fieldWarn.error} />
        </>
      ) : (
        <div className="smycles-field-value min-w-0 text-ink" id={`profile-merge-value-${side}-${diff.key}`}>
          {display || "—"}
        </div>
      )}
    </div>
  );
}

export function ProfileMergeField({
  fieldKey,
  type = "text",
  multiline = false,
  editable = true,
  format,
}: {
  fieldKey: string;
  // Mirrors the host field's input type (e.g. "date" for the birthday), so the
  // editable pair edits and formats the same way the field itself would.
  type?: "text" | "date" | "number" | "email" | "tel";
  multiline?: boolean;
  // A reference/multi-value field (gender, marital status, country, citizenship)
  // is read-only — its sides show `format(value)` (id → label) and are chosen
  // with the keep/use buttons, never typed.
  editable?: boolean;
  format?: (value: string) => string;
}) {
  const preview = useProfileMergeStore((s) => s.preview);
  const choices = useProfileMergeStore((s) => s.choices);
  const edits = useProfileMergeStore((s) => s.edits);
  const { activationProps } = useGuideCard(fieldKey);

  const diff = detailDiffFor(preview, fieldKey);
  if (!diff) return null;

  const isConflict = diff.action === "CONFLICT";
  const choice: ResolutionChoice = choices[diff.key] ?? defaultChoice(diff.action);
  const useLi = choice === "USE_INCOMING";
  // The saved value may have come from anywhere (typed, document import, admin)
  // and is gone once replaced, so show a neutral textual warning before save.
  const replacesSaved = replacesSavedValue(diff, choices, edits);

  // Keep the merge card visually present without using conflict/replacement
  // colour as a border signal. The badge and labels carry the state.
  const tint = "border border-ink/10 bg-ink/[0.02]";

  return (
    <div
      {...activationProps}
      className={`mt-1.5 flex flex-col gap-2 rounded-md px-3 py-2 ${tint}`}
      id={`profile-merge-field-${fieldKey}`}
    >
      <div className="flex items-center gap-2">
        <span className="smycles-field-meta rounded px-1.5 py-0.5 text-ink ring-1 ring-ink/15">
          {isConflict ? (
            <FormattedMessage id="profile.merge.badge.conflict" defaultMessage="Differs" />
          ) : (
            <FormattedMessage id="profile.merge.badge.new" defaultMessage="New" />
          )}
        </span>
        {replacesSaved ? (
          <span id={`profile-merge-replaces-${fieldKey}`} className="smycles-field-meta text-ink-muted">
            <FormattedMessage id="profile.merge.replacesSaved" defaultMessage="Replaces your saved value" />
          </span>
        ) : null}
      </div>

      {/* For a CONFLICT, show both sides so the decision reads correctly in BOTH
          directions: whichever side the choice discards is dimmed, its label
          struck. (For an ADD there is no existing value, so only the incoming
          side is shown; it dims when the user skips it.) */}
      {isConflict && diff.existingValue ? (
        <MergeSideValue
          diff={diff}
          side="existing"
          discarded={useLi}
          type={type}
          multiline={multiline}
          editable={editable}
          format={format}
        />
      ) : null}
      <MergeSideValue
        diff={diff}
        side="incoming"
        discarded={!useLi}
        type={type}
        multiline={multiline}
        editable={editable}
        format={format}
      />

      <MergeGuideControls stepKey={diff.key} action={diff.action} />
    </div>
  );
}
